/**
 * Breadcrumb Navigation Component (Issue #190)
 *
 * Provides hierarchical navigation context for deep routes.
 * Automatically generates breadcrumbs from the current URL path.
 */

import HomeIcon from '@mui/icons-material/Home';
import NavigateNext from '@mui/icons-material/NavigateNext';
import { Link, MuiBreadcrumbs, Typography, useI18n, useMediaQuery, useTheme } from '@ops-matrix/design-system-ui';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useBreadcrumbOverrides } from '../../contexts/BreadcrumbContext';

interface BreadcrumbConfig {
  // Map of path segments to translation keys
  labelKeys: Record<string, string>;
  // Map of path patterns to custom label generators
  dynamicLabels?: Record<string, (segment: string) => string>;
}

const DEFAULT_CONFIG: BreadcrumbConfig = {
  labelKeys: {
    dashboard: 'breadcrumb.dashboard',
    applications: 'breadcrumb.applications',
    documents: 'breadcrumb.documents',
    profile: 'breadcrumb.profile',
    records: 'breadcrumb.records',
    submit: 'breadcrumb.submit',
    onboarding: 'breadcrumb.onboarding',
    proposals: 'breadcrumb.proposals',
    quotes: 'breadcrumb.quotes',
    new: 'breadcrumb.new',
    edit: 'breadcrumb.edit',
  },
  dynamicLabels: {
    // Pattern: UUID or ID-like segments
    id: (segment: string) => {
      // If it looks like a UUID, shorten it
      if (segment.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
        return `#${segment.slice(0, 8)}`;
      }
      // If it's a number, prefix with #
      if (segment.match(/^\d+$/)) {
        return `#${segment}`;
      }
      // Otherwise, capitalize and format
      return segment
        .split(/[-_]/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
  },
};

/**
 * Generate breadcrumb items from the current path
 */
function generateBreadcrumbs(
  pathname: string,
  t: (key: string) => string,
  config: BreadcrumbConfig = DEFAULT_CONFIG,
  overrides: Record<string, string> = {},
) {
  const paths = pathname.split('/').filter(Boolean);
  const breadcrumbs: Array<{ label: string; path: string; isLast: boolean }> = [];

  // Always start with home/dashboard
  breadcrumbs.push({
    label: t('breadcrumb.home'),
    path: '/dashboard',
    isLast: false,
  });

  // Build breadcrumbs from path segments
  let currentPath = '';
  paths.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const isLast = index === paths.length - 1;

    // Determine label — check overrides first
    let label: string;
    if (overrides[segment]) {
      label = overrides[segment];
    } else if (config.labelKeys[segment]) {
      label = t(config.labelKeys[segment]);
    } else if (config.dynamicLabels?.id) {
      label = config.dynamicLabels.id(segment);
    } else {
      // Fallback: capitalize first letter
      label = segment.charAt(0).toUpperCase() + segment.slice(1);
    }

    breadcrumbs.push({
      label,
      path: currentPath,
      isLast,
    });
  });

  return breadcrumbs;
}

export default function Breadcrumbs() {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useI18n();
  const { overrides } = useBreadcrumbOverrides();

  // Don't show breadcrumbs on dashboard or root
  if (location.pathname === '/' || location.pathname === '/dashboard') {
    return null;
  }

  const breadcrumbs = generateBreadcrumbs(location.pathname, t, DEFAULT_CONFIG, overrides);

  // On mobile, only show the last 2 breadcrumbs
  const visibleBreadcrumbs = isMobile ? breadcrumbs.slice(-2) : breadcrumbs;

  return (
    <MuiBreadcrumbs
      separator={<NavigateNext fontSize="small" />}
      aria-label="breadcrumb"
      sx={{
        mb: 2,
        '& .MuiBreadcrumbs-separator': {
          color: 'text.secondary',
        },
      }}
    >
      {visibleBreadcrumbs.map((crumb, index) => {
        // First breadcrumb shows home icon
        if (index === 0 && !isMobile) {
          return crumb.isLast ? (
            <Typography key={crumb.path} color="textPrimary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <HomeIcon fontSize="small" />
              {crumb.label}
            </Typography>
          ) : (
            <Link
              key={crumb.path}
              component={RouterLink}
              to={crumb.path}
              underline="hover"
              color="inherit"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <HomeIcon fontSize="small" />
              {crumb.label}
            </Link>
          );
        }

        // Last breadcrumb is not clickable
        if (crumb.isLast) {
          return (
            <Typography key={crumb.path} sx={{ color: 'text.primary', fontWeight: 500 }}>
              {crumb.label}
            </Typography>
          );
        }

        // Other breadcrumbs are clickable links
        return (
          <Link key={crumb.path} component={RouterLink} to={crumb.path} underline="hover" color="inherit">
            {crumb.label}
          </Link>
        );
      })}
    </MuiBreadcrumbs>
  );
}
